import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery, useInvalidateSupabase } from '@shared/supabase/hooks';
import { Input, Skeleton, SectionErrorBoundary } from '@shared/ui';
import UserAccessDrawer from './UserAccessDrawer';

interface OrgRow { id: string; name: string; access_status: string; tier: string | null; user_count: number; brand_count: number; features: string[]; last_seen: string | null; email: string | null }

// Compact "last online" — green if within a week, muted date otherwise, '—' if never.
function lastSeen(iso: string | null): { txt: string; color: string } {
  if (!iso) return { txt: '—', color: '#556' };
  const days = (Date.now() - new Date(iso).getTime()) / 86_400_000;
  const txt = days < 1 ? 'сегодня' : days < 2 ? 'вчера' : days < 8 ? `${Math.floor(days)} дн назад`
    : new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' });
  return { txt, color: days < 8 ? '#5fd17a' : '#8a93a6' };
}

const STATUS_COLOR: Record<string, string> = { active: '#2ecc71', pending: '#f1c40f', suspended: '#e74c3c' };

/** Object-first B2B admin: the primary unit is the BUSINESS (organization); users,
 *  brands and features are managed inside it. (Individual accounts live in "Все аккаунты".) */
export default function Businesses() {
  const { t } = useTranslation('admin');
  const [search, setSearch] = useState('');
  const [openOrg, setOpenOrg] = useState<OrgRow | null>(null);

  const { data, isLoading, error } = useSupabaseQuery<OrgRow[]>(
    ['admin-orgs-list'],
    async () => supabase.rpc('admin_list_organizations'),
  );
  const q = search.trim().toLowerCase();
  // Pending orgs live in the funnel above (Компании ждут активации) — this roster is the
  // ACTIVE/managed businesses only, so they aren't listed twice.
  const rows = (data ?? [])
    .filter((o) => o.access_status !== 'pending')
    .filter((o) => !q || (o.name ?? '').toLowerCase().includes(q));

  return (
    <SectionErrorBoundary name="admin-businesses">
      <div style={{ marginBottom: 14 }}>
        <Input placeholder={t('biz.search', { defaultValue: 'Поиск компании…' }) as string} value={search} onChange={(e) => setSearch(e.target.value)} style={{ minWidth: 260 }} />
      </div>

      {isLoading && <Skeleton height={240} />}
      {error && <div style={{ color: '#e74c3c' }}>{String((error as Error)?.message ?? error)}</div>}
      {!isLoading && !error && rows.length === 0 && <div style={{ color: '#888', padding: 24, textAlign: 'center' }}>{t('biz.empty', { defaultValue: 'Компаний нет.' })}</div>}

      {rows.length > 0 && (
        <div style={{ overflowX: 'auto', border: '1px solid rgba(15,45,100,0.4)', borderRadius: 8 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--fs-body)' }}>
            <thead style={{ background: 'rgba(15,45,100,0.2)' }}>
              <tr>
                <th style={th}>{t('biz.company', { defaultValue: 'Компания' })}</th>
                <th style={th}>{t('biz.email', { defaultValue: 'Email' })}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('biz.users', { defaultValue: 'Пользователи' })}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('biz.brands', { defaultValue: 'Бренды' })}</th>
                <th style={th}>{t('biz.access', { defaultValue: 'Доступ' })}</th>
                <th style={th}>{t('biz.lastSeen', { defaultValue: 'Был онлайн' })}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('biz.features', { defaultValue: 'Функции' })}</th>
                <th style={th} />
              </tr>
            </thead>
            <tbody>
              {rows.map((o) => (
                <tr key={o.id} onClick={() => setOpenOrg(o)} style={{ borderTop: '1px solid rgba(15,45,100,0.3)', cursor: 'pointer' }}>
                  <td style={{ ...td, fontWeight: 'var(--fw-medium)' as never, color: '#dde' }}>{o.name}</td>
                  <td style={{ ...td, color: o.email ? '#9fb4d6' : '#556', whiteSpace: 'nowrap' }}>{o.email ?? '—'}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{o.user_count}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{o.brand_count}</td>
                  <td style={td}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: STATUS_COLOR[o.access_status] ?? '#888' }} />{o.access_status}
                    </span>
                  </td>
                  <td style={{ ...td, color: lastSeen(o.last_seen).color, whiteSpace: 'nowrap' }}>{lastSeen(o.last_seen).txt}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: o.features.length ? '#cbd5ff' : '#666' }}>
                    {o.features.length || '—'}
                  </td>
                  <td style={{ ...td, color: '#7f8aa3' }}>›</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openOrg && <BusinessDrawer org={openOrg} onClose={() => setOpenOrg(null)} />}
    </SectionErrorBoundary>
  );
}

function BusinessDrawer({ org, onClose }: { org: OrgRow; onClose: () => void }) {
  const { t } = useTranslation('admin');
  const [drawerUser, setDrawerUser] = useState<{ id: string; name: string | null } | null>(null);

  const usersQ = useSupabaseQuery<{ id: string; display_name: string | null; access_status: string | null; user_roles: { role: string }[] | null }[]>(
    ['admin-org-users', org.id],
    async () => supabase.from('profiles').select('id, display_name, access_status, user_roles!user_roles_profile_fkey(role)').eq('organization_id', org.id),
  );
  const brandsQ = useSupabaseQuery<{ brand_id: string; name: string }[]>(
    ['admin-org-brands', org.id],
    async () => supabase.rpc('admin_org_brands', { p_org: org.id }),
  );

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 50, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'flex-end' }}>
      <aside onClick={(e) => e.stopPropagation()} style={{ width: 'min(460px, 94vw)', height: '100%', overflowY: 'auto', background: 'var(--bg-elev,#12151c)', borderLeft: '1px solid rgba(15,45,100,0.5)', padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>{org.name}</h3>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 0, color: '#9aa', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--fs-small)', color: '#9aa' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: STATUS_COLOR[org.access_status] ?? '#888' }} />{org.access_status}{org.tier ? ` · ${org.tier}` : ''}
        </span>

        <Section title={t('biz.usersSection', { defaultValue: 'Пользователи' })}>
          {(usersQ.data ?? []).length === 0 ? <Muted>{t('biz.noUsers', { defaultValue: 'Нет пользователей.' })}</Muted> : (usersQ.data ?? []).map((u) => {
            const role = (u.user_roles ?? [])[0]?.role ?? 'precustomer';
            return (
              <div key={u.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <div>
                  <div style={{ fontSize: 'var(--fs-body)' }}>{u.display_name ?? '—'}</div>
                  <div style={{ fontSize: 'var(--fs-caption)', color: '#8a93a6' }}>{role} · {u.access_status ?? 'pending'}</div>
                </div>
                <button type="button" onClick={() => setDrawerUser({ id: u.id, name: u.display_name })} style={manageBtn}>{t('users.manageAccess', { defaultValue: 'Доступ' })}</button>
              </div>
            );
          })}
        </Section>

        <Section title={`${t('biz.brandsSection', { defaultValue: 'Бренды' })} · ${(brandsQ.data ?? []).length}`}>
          {(brandsQ.data ?? []).length === 0 ? <Muted>{t('biz.noBrands', { defaultValue: 'Нет привязанных брендов.' })}</Muted>
            : <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{(brandsQ.data ?? []).map((b) => <Chip key={b.brand_id}>{b.name}</Chip>)}</div>}
        </Section>

        <FeatureToggles orgId={org.id} />
      </aside>

      {drawerUser && <UserAccessDrawer userId={drawerUser.id} displayName={drawerUser.name} onClose={() => setDrawerUser(null)} />}
    </div>
  );
}

// Local YYYY-MM-DD for <input type="date"> (avoids UTC off-by-one near midnight).
function toDateInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function daysLeft(iso: string): number {
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
}

// Actionable feature grants — human labels, a switch per feature, and a "paid until"
// date per enabled PAID feature. Setting a date = access auto-expires on that date
// (organization_features.expires_at is enforced live by has_feature + the JWT hook).
function FeatureToggles({ orgId }: { orgId: string }) {
  const { t } = useTranslation('admin');
  const invalidate = useInvalidateSupabase();
  const [busy, setBusy] = useState<string | null>(null);
  const catQ = useSupabaseQuery<{ key: string; label_ru: string | null; is_premium: boolean }[]>(
    ['access-features'],
    async () => supabase.from('access_features').select('key, label_ru, is_premium').order('sort_order'),
  );
  const grantsQ = useSupabaseQuery<{ feature_key: string; allowed: boolean; expires_at: string | null }[]>(
    ['admin-org-features', orgId],
    async () => supabase.rpc('admin_org_features', { p_org: orgId }),
  );
  const grants = new Map((grantsQ.data ?? []).map((g) => [g.feature_key, g] as const));

  const setFeat = async (key: string, allowed: boolean, expires: string | null) => {
    setBusy(key);
    const { error } = await supabase.rpc('admin_set_org_feature', { p_org: orgId, p_key: key, p_allowed: allowed, p_expires: expires });
    setBusy(null);
    if (error) { alert(error.message); return; }
    invalidate(['admin-org-features', orgId]);
    invalidate(['admin-orgs-list']);
  };

  // One-click tier switch: grants the whole Pro/Premium feature set (or strips it) with
  // a shared "paid until" date, instead of toggling each feature by hand.
  const has = (k: string) => !!grants.get(k)?.allowed;
  const currentTier: 'free' | 'pro' | 'premium' =
    (has('an_price') || has('act_request')) ? 'premium' : has('an_social') ? 'pro' : 'free';
  const [tierBusy, setTierBusy] = useState(false);
  const [tierDate, setTierDate] = useState('');
  const applyTier = async (tier: 'free' | 'pro' | 'premium', expires: string | null) => {
    setTierBusy(true);
    const { error } = await supabase.rpc('admin_set_org_tier', { p_org: orgId, p_tier: tier, p_expires: expires });
    setTierBusy(false);
    if (error) { alert(error.message); return; }
    invalidate(['admin-org-features', orgId]);
    invalidate(['admin-orgs-list']);
  };
  const setTier = (tier: 'free' | 'pro' | 'premium') =>
    applyTier(tier, tier !== 'free' && tierDate ? new Date(`${tierDate}T23:59:59`).toISOString() : null);
  // "Trial" = full Premium with a 7-day expiry (matches provision_trial_features).
  const startTrial = () => applyTier('premium', new Date(Date.now() + 7 * 86_400_000).toISOString());

  // Current runway: soonest expiry among the org's paid features (a trial or a paid-until).
  const datedExp = (catQ.data ?? [])
    .filter((f) => f.is_premium)
    .map((f) => grants.get(f.key))
    .filter((g): g is { feature_key: string; allowed: boolean; expires_at: string } => !!g?.allowed && !!g?.expires_at);
  const runwayDays = datedExp.length ? Math.min(...datedExp.map((g) => daysLeft(g.expires_at))) : null;

  return (
    <Section title={t('biz.featuresSection', { defaultValue: 'Доступ к функциям' })}>
      <div style={{ marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ fontSize: 'var(--fs-caption)', color: '#8a93a6', marginBottom: 6 }}>{t('biz.tier', { defaultValue: 'Тариф (весь набор функций одним кликом)' })}</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {(['free', 'pro', 'premium'] as const).map((tk) => (
            <button key={tk} type="button" disabled={tierBusy} onClick={() => setTier(tk)}
              style={{ cursor: tierBusy ? 'default' : 'pointer', fontFamily: 'inherit', padding: '5px 14px', borderRadius: 6, fontSize: 'var(--fs-small)', fontWeight: 600,
                color: currentTier === tk ? '#0d1117' : '#cbd5ff',
                background: currentTier === tk ? '#2ecc71' : 'rgba(74,158,255,0.12)',
                border: `1px solid ${currentTier === tk ? 'rgba(46,204,113,0.5)' : 'rgba(74,158,255,0.35)'}`, opacity: tierBusy ? 0.5 : 1 }}>
              {tk === 'free' ? 'Free' : tk === 'pro' ? 'Pro' : 'Premium'}
            </button>
          ))}
          <button type="button" disabled={tierBusy} onClick={startTrial}
            title={t('biz.trialTitle', { defaultValue: 'Premium на 7 дней' }) as string}
            style={{ cursor: tierBusy ? 'default' : 'pointer', fontFamily: 'inherit', padding: '5px 14px', borderRadius: 6, fontSize: 'var(--fs-small)', fontWeight: 600, color: '#d8b667', background: 'rgba(201,154,58,0.12)', border: '1px solid rgba(201,154,58,0.45)', opacity: tierBusy ? 0.5 : 1 }}>
            {t('biz.trial7', { defaultValue: 'Пробный 7 дн' })}
          </button>
          <span style={{ marginLeft: 6, fontSize: 'var(--fs-caption)', color: '#8a93a6' }}>{t('biz.paidUntil', { defaultValue: 'Оплачено до' })}</span>
          <input type="date" value={tierDate} onChange={(e) => setTierDate(e.target.value)}
            style={{ background: 'rgba(15,45,100,0.25)', border: '1px solid rgba(74,158,255,0.3)', borderRadius: 4, color: '#cbd5ff', padding: '3px 6px', fontFamily: 'inherit', fontSize: 'var(--fs-caption)' }} />
        </div>
        <div style={{ fontSize: 'var(--fs-caption)', color: runwayDays != null && runwayDays <= 7 ? '#d8b667' : '#8a93a6', marginTop: 6 }}>
          {t('biz.tierNow', { defaultValue: 'Сейчас' })}: <b style={{ color: '#cbd5ff' }}>{currentTier === 'free' ? 'Free' : currentTier === 'pro' ? 'Pro' : 'Premium'}</b>
          {currentTier !== 'free' && (runwayDays != null
            ? ` · ${t('biz.trialLeft', { defaultValue: 'пробный/оплачен, осталось {{n}} дн.', n: runwayDays })}`
            : ` · ${t('biz.permanent', { defaultValue: 'бессрочно' })}`)}
        </div>
        <div style={{ fontSize: 'var(--fs-caption)', color: '#667', marginTop: 4 }}>{t('biz.tierHint', { defaultValue: 'Пусто = бессрочно (оплачено). Free снимает все платные функции.' })}</div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {(catQ.data ?? []).map((f) => {
          const g = grants.get(f.key);
          const isOn = !!g?.allowed;
          const exp = g?.expires_at ?? null;
          const left = exp ? daysLeft(exp) : null;
          return (
            <div key={f.key} style={{ padding: '7px 0', borderTop: '1px solid rgba(255,255,255,0.06)', opacity: busy === f.key ? 0.5 : 1 }}>
              <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: busy ? 'default' : 'pointer' }}>
                <span style={{ fontSize: 'var(--fs-body)', color: isOn ? '#dde' : '#8a93a6' }}>
                  {f.label_ru ?? f.key}{!f.is_premium && <span style={{ color: '#6b7a55', fontSize: 'var(--fs-caption)' }}> · бесплатно</span>}
                </span>
                <input type="checkbox" checked={isOn} disabled={!!busy} onChange={() => setFeat(f.key, !isOn, exp)} style={{ width: 16, height: 16, cursor: 'pointer' }} />
              </label>
              {/* Paid features get an expiry control; free ones (an_my) are always permanent. */}
              {isOn && f.is_premium && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 5, fontSize: 'var(--fs-caption)' }}>
                  <span style={{ color: '#8a93a6' }}>{t('biz.paidUntil', { defaultValue: 'Оплачено до' })}:</span>
                  <input
                    type="date"
                    value={toDateInput(exp)}
                    disabled={!!busy}
                    onChange={(e) => setFeat(f.key, true, e.target.value ? new Date(`${e.target.value}T23:59:59`).toISOString() : null)}
                    style={{ background: 'rgba(15,45,100,0.25)', border: '1px solid rgba(74,158,255,0.3)', borderRadius: 4, color: '#cbd5ff', padding: '2px 6px', fontFamily: 'inherit', fontSize: 'var(--fs-caption)' }}
                  />
                  {exp
                    ? <span style={{ color: left! <= 0 ? '#e74c3c' : left! <= 7 ? '#f1c40f' : '#7f8aa3' }}>{left! <= 0 ? 'истёк' : `${left} дн.`}</span>
                    : <span style={{ color: '#7f8aa3' }}>{t('biz.permanent', { defaultValue: 'бессрочно' })}</span>}
                  {exp && <button type="button" disabled={!!busy} onClick={() => setFeat(f.key, true, null)} style={{ background: 'none', border: 0, color: '#8a93a6', cursor: 'pointer', textDecoration: 'underline', fontSize: 'var(--fs-caption)' }}>{t('biz.makePermanent', { defaultValue: 'сделать бессрочным' })}</button>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Section>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 'var(--fs-small)', color: '#8a93a6', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 8 }}>{title}</div>
      {children}
    </div>
  );
}
const Muted = ({ children }: { children: React.ReactNode }) => <div style={{ fontSize: 'var(--fs-small)', color: '#666' }}>{children}</div>;
function Chip({ children }: { children: React.ReactNode }) {
  return <span style={{ padding: '2px 9px', borderRadius: 999, fontSize: 'var(--fs-caption)', color: '#bcd9ff', background: 'rgba(74,158,255,0.12)', border: '1px solid rgba(74,158,255,0.3)' }}>{children}</span>;
}

const th: React.CSSProperties = { padding: 10, textAlign: 'left' };
const td: React.CSSProperties = { padding: 10 };
const manageBtn: React.CSSProperties = { cursor: 'pointer', fontFamily: 'inherit', padding: '4px 10px', borderRadius: 4, fontSize: 'var(--fs-small)', color: '#bcd0ff', background: 'rgba(74,158,255,0.15)', border: '1px solid rgba(74,158,255,0.4)' };
